import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronLeft, ChevronRight, Settings } from "lucide-react";
import clsx from "clsx";
import { PLAYBACK_RATES } from "@/utils/videoPreferences";
import type { HlsQualityLevel } from "@/hooks/useHlsPlayer";

export interface QualityMenuState {
  levels: HlsQualityLevel[];
  activeHeight: number | null;
  isAuto: boolean;
  isLoading: boolean;
  /**
   * Whether adaptive selection is offered. False for the legacy per-file MP4
   * path, where each rendition is a separate URL and there is no ABR ladder
   * to hand the decision back to.
   */
  allowAuto?: boolean;
}

export interface SettingsMenuProps {
  quality: QualityMenuState;
  playbackRate: number;
  onQualityChange: (height: number | null) => void;
  onPlaybackRateChange: (rate: number) => void;
  /** Lets the bar pin itself open while a menu is showing. */
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
}

type MenuPage = "root" | "quality" | "speed";

const formatRate = (rate: number): string => `${rate}×`;

/**
 * The ⚙ popover holding quality and playback speed. Two-level rather than one
 * flat list: the root row doubles as a readout of the current setting, which
 * is the thing a viewer most often opens this menu to check.
 *
 * The quality section is omitted entirely when no HLS levels are known — on
 * the native-HLS path (`useHlsPlayer` falls back to it when hls.js is
 * unsupported) variant switching is the browser's to make and `selectQuality`
 * is a no-op, so offering the control would be a lie.
 */
export function SettingsMenu({
  quality,
  playbackRate,
  onQualityChange,
  onPlaybackRateChange,
  onOpenChange,
  disabled = false,
}: SettingsMenuProps) {
  const { t } = useTranslation("pages");
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState<MenuPage>("root");
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const hasQuality = quality.levels.length > 0;

  const close = useCallback(
    (returnFocus: boolean) => {
      setIsOpen(false);
      setPage("root");
      onOpenChange?.(false);
      if (returnFocus) {
        triggerRef.current?.focus();
      }
    },
    [onOpenChange],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        close(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // Stop the keypress before it reaches the container's shortcut
        // handler or, in fullscreen, the browser's exit-fullscreen default.
        event.preventDefault();
        event.stopPropagation();
        close(true);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [close, isOpen]);

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    setPage("root");
    onOpenChange?.(next);
  };

  const allowAuto = quality.allowAuto !== false;
  // The Auto *option* names itself, annotated with the height ABR has settled
  // on only while Auto is the active choice. Distinct from the root row's
  // readout below, which reports the pinned height when the viewer has taken
  // manual control — conflating the two made the Auto row read "720p".
  const autoOptionLabel =
    quality.isAuto && quality.activeHeight !== null
      ? t("sessions.videoControls.autoWithHeight", {
          height: quality.activeHeight,
        })
      : t("sessions.videoControls.auto");
  const currentQualityLabel =
    !quality.isAuto && quality.activeHeight !== null
      ? `${quality.activeHeight}p`
      : autoOptionLabel;

  const rowClass =
    "flex w-full items-center justify-between gap-6 rounded px-3 py-2 text-sm text-white transition-colors hover:bg-white/10";
  const optionClass =
    "flex w-full items-center gap-2 rounded px-3 py-2 text-sm transition-colors hover:bg-white/10";

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t("sessions.videoControls.settings")}
        title={t("sessions.videoControls.settings")}
        className="rounded p-2 text-white transition-colors hover:text-[#60a5fa] disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Settings className="h-4 w-4" aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute bottom-full right-0 mb-2 min-w-44 rounded-lg border border-[#334155] bg-[#020617]/95 p-1 shadow-lg backdrop-blur"
        >
          {page === "root" && (
            <>
              {hasQuality && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => setPage("quality")}
                  className={rowClass}
                >
                  <span>{t("sessions.videoControls.quality")}</span>
                  <span className="flex items-center gap-1 text-slate-300">
                    {currentQualityLabel}
                    <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => setPage("speed")}
                className={rowClass}
              >
                <span>{t("sessions.videoControls.speed")}</span>
                <span className="flex items-center gap-1 text-slate-300">
                  {playbackRate === 1
                    ? t("sessions.videoControls.normalSpeed")
                    : formatRate(playbackRate)}
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
              </button>
            </>
          )}

          {page !== "root" && (
            <button
              type="button"
              onClick={() => setPage("root")}
              className="mb-1 flex w-full items-center gap-1 border-b border-[#334155] px-3 py-2 text-xs font-medium text-slate-300 transition-colors hover:text-white"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
              {page === "quality"
                ? t("sessions.videoControls.quality")
                : t("sessions.videoControls.speed")}
            </button>
          )}

          {page === "quality" && (
            <>
              {allowAuto && (
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={quality.isAuto}
                  onClick={() => {
                    onQualityChange(null);
                    close(true);
                  }}
                  className={clsx(
                    optionClass,
                    quality.isAuto ? "text-[#60a5fa]" : "text-white",
                  )}
                >
                  <Check
                    className={clsx(
                      "h-3.5 w-3.5 shrink-0",
                      quality.isAuto ? "opacity-100" : "opacity-0",
                    )}
                    aria-hidden="true"
                  />
                  {autoOptionLabel}
                </button>
              )}
              {quality.levels.map((level) => {
                const isSelected =
                  !quality.isAuto && quality.activeHeight === level.height;
                return (
                  <button
                    key={level.height}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isSelected}
                    onClick={() => {
                      onQualityChange(level.height);
                      close(true);
                    }}
                    className={clsx(
                      optionClass,
                      isSelected ? "text-[#60a5fa]" : "text-white",
                    )}
                  >
                    <Check
                      className={clsx(
                        "h-3.5 w-3.5 shrink-0",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                      aria-hidden="true"
                    />
                    {`${level.height}p`}
                  </button>
                );
              })}
            </>
          )}

          {page === "speed" &&
            PLAYBACK_RATES.map((rate) => {
              const isSelected = playbackRate === rate;
              return (
                <button
                  key={rate}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isSelected}
                  onClick={() => {
                    onPlaybackRateChange(rate);
                    close(true);
                  }}
                  className={clsx(
                    optionClass,
                    isSelected ? "text-[#60a5fa]" : "text-white",
                  )}
                >
                  <Check
                    className={clsx(
                      "h-3.5 w-3.5 shrink-0",
                      isSelected ? "opacity-100" : "opacity-0",
                    )}
                    aria-hidden="true"
                  />
                  {rate === 1
                    ? t("sessions.videoControls.normalSpeed")
                    : formatRate(rate)}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}

export default SettingsMenu;
